// Version: 2.5
// v2.5: Conditional Access checks say Enforced, Report only (set up but not switched on)
//       or Missing, from ca_summary.checks (m365graph-check v2.6); the header counts
//       enforced and report-only policies. Report-only policies showed as Missing.
//       MFA Enabled says what it counts ("of 13 licensed"): only licensed members,
//       so it didn't add up to the enabled users. Storage notes when Microsoft 365
//       hides user names (totals only).
// v2.4: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
import { useState, useEffect, useCallback } from 'react';
import { authFetch } from './auth';
import { useAuth } from './auth';
import './Productivity.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) + ' GMT'; }
  catch { return d; }
}

function fmtDateShort(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return d; }
}

function ScoreBadge({ pct }) {
  const color = pct >= 80 ? '#30d158' : pct >= 60 ? '#e6b800' : '#ff2d55';
  return (
    <div className="score-ring" style={{ '--score-color': color }}>
      <svg viewBox="0 0 36 36" className="score-svg">
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2.5" />
        <circle cx="18" cy="18" r="15.9" fill="none" stroke={color} strokeWidth="2.5"
          strokeDasharray={`${pct} ${100 - pct}`} strokeDashoffset="25" strokeLinecap="round" />
      </svg>
      <div className="score-val">{pct}%</div>
    </div>
  );
}

function StatTile({ label, value, sub, color, tileColor }) {
  return (
    <div className={`prod-stat-tile${tileColor ? ' ' + tileColor : ''}`}>
      <div className="prod-stat-value" style={color ? { color } : {}}>{value ?? '—'}</div>
      <div className="prod-stat-label">{label}</div>
      {sub && <div className="prod-stat-sub">{sub}</div>}
    </div>
  );
}

function ProviderPill({ provider }) {
  const providerColor = provider === 'm365' ? '#00a4ef' : '#4285f4';
  const providerLabel = provider === 'm365' ? 'M365' : 'Google';
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
      background: `${providerColor}20`, border: `1px solid ${providerColor}50`,
      color: providerColor, letterSpacing: '0.05em',
    }}>{providerLabel}</span>
  );
}

// ── Severity badge ─────────────────────────────────────────────────────────────

function SeverityBadge({ severity }) {
  const map = {
    critical: { color: '#ff2d55', label: 'Critical' },
    high:     { color: '#ff6b35', label: 'High' },
    medium:   { color: '#e6b800', label: 'Medium' },
    low:      { color: '#86868b', label: 'Low' },
  };
  const { color, label } = map[severity] || { color: '#86868b', label: severity };
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
      background: `${color}20`, border: `1px solid ${color}50`, color, letterSpacing: '0.04em',
    }}>{label}</span>
  );
}

// ── Finding card ───────────────────────────────────────────────────────────────

function FindingCard({ finding }) {
  const [expanded, setExpanded] = useState(false);
  const { passed, title, severity, description, recommendation, fix_url, affected_count, affected_objects, notes } = finding;
  const statusColor = passed ? '#30d158' : severity === 'critical' ? '#ff2d55' : severity === 'high' ? '#ff6b35' : severity === 'medium' ? '#e6b800' : '#86868b';

  return (
    <div style={{
      background: 'var(--card-bg)', border: `1px solid var(--border)`,
      borderLeft: `3px solid ${statusColor}`, borderRadius: 8,
      padding: '12px 14px', marginBottom: 8,
    }}>
      <div
        style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, cursor: 'pointer' }}
        onClick={() => setExpanded(e => !e)}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{title}</span>
            {!passed && <SeverityBadge severity={severity} />}
            {passed && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: '#30d15820', border: '1px solid #30d15850', color: '#30d158' }}>
                Passed
              </span>
            )}
            {affected_count > 0 && (
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{affected_count} affected</span>
            )}
          </div>
          {!expanded && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4 }}>{description}</div>
          )}
        </div>
        <span style={{ fontSize: 10, color: 'var(--text-muted)', flexShrink: 0, marginTop: 2 }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
          <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.5, marginBottom: 8 }}>{description}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 4 }}>Recommendation</div>
          <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.5, marginBottom: notes ? 8 : 0 }}>{recommendation}</div>
          {notes && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, fontStyle: 'italic' }}>{notes}</div>
          )}
          {fix_url && (
            <div style={{ marginTop: 10 }}>
              <a href={fix_url} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 11, color: 'var(--accent)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <i className="ti ti-external-link" style={{ fontSize: 12 }} />
                Fix this in admin portal
              </a>
            </div>
          )}
          {affected_objects?.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 6 }}>
                Affected ({affected_objects.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 160, overflowY: 'auto' }}>
                {affected_objects.map((o, i) => (
                  <div key={i} style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', padding: '2px 0' }}>
                    {o.upn || o.device || o.name || o.app_name || JSON.stringify(o)}
                    {o.risk_level && <span style={{ marginLeft: 8, color: '#ff2d55', fontWeight: 600 }}>{o.risk_level}</span>}
                    {o.roles && <span style={{ marginLeft: 8, color: 'var(--text-muted)', fontSize: 10 }}>{Array.isArray(o.roles) ? o.roles.join(', ') : o.roles}</span>}
                    {o.risky_scopes?.length > 0 && <span style={{ marginLeft: 8, color: '#e6b800', fontSize: 10 }}>{o.risky_scopes.join(', ')}</span>}
                    {o.client_app && <span style={{ marginLeft: 8, color: 'var(--text-muted)', fontSize: 10 }}>{o.client_app}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Status pill ────────────────────────────────────────────────────────────────

function StatusPill({ ok, trueLabel = 'Yes', falseLabel = 'No', nullLabel = 'Unknown' }) {
  if (ok === null || ok === undefined) {
    return <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{nullLabel}</span>;
  }
  const color = ok ? '#30d158' : '#ff2d55';
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
      background: `${color}20`, border: `1px solid ${color}50`, color,
    }}>{ok ? trueLabel : falseLabel}</span>
  );
}

// ── M365 Tenant View ───────────────────────────────────────────────────────────

function M365TenantView({ slug, tenantId, isAdmin }) {
  const [data, setData]       = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [userFilter, setUserFilter] = useState('');
  const [running, setRunning] = useState(false);
  const [runLog, setRunLog]   = useState([]);
  const [watchedSignins, setWatchedSignins]   = useState([]);
  const [watchedAccounts, setWatchedAccounts] = useState([]);
  const [alertCfg, setAlertCfg]               = useState(null);

  const loadResults = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [mainRes, watchedRes, acctRes, cfgRes] = await Promise.all([
        authFetch(`/api/clients/${slug}/productivity/results?tenant_id=${tenantId}`),
        authFetch(`/api/clients/${slug}/m365/watched-signins?tenant_id=${tenantId}`),
        authFetch(`/api/clients/${slug}/watched-accounts`),
        authFetch('/api/admin/alerts/config'),
      ]);
      const d  = await mainRes.json();
      const wd = await watchedRes.json();
      const wa = await acctRes.json();
      const ac = await cfgRes.json();
      if (!mainRes.ok) { setError(d.error || 'Failed to load'); setData(null); return; }
      setData(d.data);
      setLastRun(d.last_run);
      setWatchedSignins(wd.events || []);
      setWatchedAccounts(wa.watched_accounts || []);
      setAlertCfg(ac);
    } catch { setError('Failed to load results'); setData(null); }
    finally { setLoading(false); }
  }, [slug, tenantId]);

  useEffect(() => { loadResults(); }, [loadResults]);

  // Auto-refresh watched sign-ins every 2 minutes when Identity tab is active
  useEffect(() => {
    if (activeTab !== 'identity') return;
    const interval = setInterval(async () => {
      try {
        const r  = await authFetch(`/api/clients/${slug}/m365/watched-signins?tenant_id=${tenantId}`);
        const wd = await r.json();
        setWatchedSignins(wd.events || []);
      } catch {}
    }, 120000);
    return () => clearInterval(interval);
  }, [activeTab, slug]);

  const handleRun = async () => {
    if (running) return;
    setRunning(true); setRunLog([]);
    try {
      const r = await authFetch(`/api/clients/${slug}/productivity/tenants/${tenantId}/run`, { method: 'POST' });
      if (r.ok) {
        setRunLog(['Check started — results will update shortly']);
        setTimeout(() => loadResults(), 30000);
      }
    } catch { setRunLog(['Failed to start run']); }
    finally { setRunning(false); }
  };

  // Data extraction
  const summary        = data?.summary || {};
  const users          = data?.users || [];
  const licences       = data?.licences || [];
  const spSites        = data?.sharepoint_sites || [];
  const riskyUsers     = data?.risky_users || [];
  const secScore       = summary?.secure_score || {};
  const storage        = summary?.tenant_storage || {};
  const findings       = data?.findings || [];
  const caSummary      = summary?.ca_summary || data?.ca_summary || {};
  const caPolicies     = data?.ca_policies || [];
  const legacyUsers    = data?.legacy_auth_users || [];
  const oauthGrants    = data?.oauth_grants || [];
  const intuneDevices  = data?.intune_devices || [];
  const intuneSummary  = summary?.intune_summary || data?.intune_summary || {};
  const globalAdmins   = data?.global_admin_users || [];
  const adminsNoMfa    = data?.admins_no_mfa || [];
  const assessScore    = summary?.assessment_score ?? data?.assessment_score ?? null;
  const sdEnabled      = summary?.security_defaults_enabled ?? null;

  // Derived counts for tab labels
  const failedFindings  = findings.filter(f => !f.passed);
  const criticalCount   = failedFindings.filter(f => f.severity === 'critical').length;
  const highRiskGrants  = oauthGrants.filter(g => g.is_high_risk);
  const noncompliantDev = intuneDevices.filter(d => d.compliance === 'noncompliant');

  const filteredUsers = users.filter(u => {
    const q = userFilter.toLowerCase();
    return !q || u.name?.toLowerCase().includes(q) || u.upn?.toLowerCase().includes(q) || u.department?.toLowerCase().includes(q);
  });

  // Build tab list dynamically based on available data
  const TABS = [
    { id: 'overview',  label: 'Overview' },
    { id: 'security',  label: `Security${failedFindings.length > 0 ? ` (${failedFindings.length})` : ''}` },
    { id: 'identity',  label: 'Identity' },
    { id: 'users',     label: `Users (${users.length})` },
    { id: 'licences',  label: 'Licences' },
    { id: 'sharepoint',label: 'SharePoint' },
    ...(intuneSummary.available ? [{ id: 'devices', label: `Devices (${intuneSummary.total ?? 0})` }] : []),
    ...(oauthGrants.length > 0  ? [{ id: 'apps',    label: `Apps${highRiskGrants.length > 0 ? ` (${highRiskGrants.length} risk)` : ''}` }] : []),
    ...(riskyUsers.length > 0   ? [{ id: 'risky',   label: `Risky Users (${riskyUsers.length})` }] : []),
  ];

  const assessColor = assessScore == null ? '#86868b' : assessScore >= 80 ? '#30d158' : assessScore >= 60 ? '#e6b800' : '#ff2d55';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {lastRun ? `Updated ${fmtDate(lastRun)}` : 'No data yet'}
        </div>
        {isAdmin && (
          <button className={`btn btn-secondary btn-sm ${running ? ' loading' : ''}`} onClick={handleRun} disabled={running}>
            {running ? 'Running…' : '↺ Refresh Now'}
          </button>
        )}
      </div>

      {running && runLog.length > 0 && (
        <div className="prod-run-log" style={{ marginBottom: 16 }}>
          {runLog.map((l, i) => <div key={i} className="prod-run-line">{l}</div>)}
        </div>
      )}

      {error && <div className="resource-error" style={{ marginBottom: 16 }}>{error}</div>}

      {!data && !loading && !error && (
        <div className="prod-empty">
          <div className="prod-empty-title">No data yet</div>
          <div className="prod-empty-sub">
            {isAdmin ? 'Click Refresh Now to run the check.' : 'Data will appear here once configured.'}
          </div>
        </div>
      )}

      {loading && <div className="resource-loading">Loading data…</div>}

      {data && !loading && (
        <>
          {/* Alert banner for critical findings */}
          {criticalCount > 0 && (
            <div style={{
              background: '#ff2d5515', border: '1px solid #ff2d5540', borderRadius: 8,
              padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <i className="ti ti-alert-triangle" style={{ color: '#ff2d55', fontSize: 16, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: '#ff2d55', fontWeight: 600 }}>
                {criticalCount} critical finding{criticalCount !== 1 ? 's' : ''} require attention
              </span>
              <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto', flexShrink: 0 }}
               
               
                onClick={() => setActiveTab('security')}
              >View</button>
            </div>
          )}

          <div className="resources-tabs" style={{ marginBottom: 24 }}>
            {TABS.map(t => (
              <button key={t.id} className={`resources-tab${activeTab === t.id ? ' active' : ''}`}
                onClick={() => setActiveTab(t.id)}>{t.label}</button>
            ))}
          </div>

          {/* ── Overview ── */}
          {activeTab === 'overview' && (
            <div className="prod-overview">
              {/* Score row — Secure Score + Assessment Score side by side */}
              <div style={{ display: 'grid', gridTemplateColumns: assessScore != null ? '1fr 1fr' : '1fr', gap: 12, marginBottom: 12 }}>
                <div className="resource-panel prod-score-panel">
                  <div className="resource-panel-header">
                    <div>
                      <div className="resource-panel-title">Microsoft Secure Score</div>
                      <div className="resource-panel-sub">
                        {secScore.current} / {secScore.maximum} points · {secScore.active_users} active users
                      </div>
                    </div>
                    {secScore.pct != null && <ScoreBadge pct={Math.round(secScore.pct)} />}
                  </div>
                </div>
                {assessScore != null && (
                  <div className="resource-panel prod-score-panel">
                    <div className="resource-panel-header">
                      <div>
                        <div className="resource-panel-title">Security Assessment</div>
                        <div className="resource-panel-sub">
                          {summary.findings_failed ?? 0} finding{summary.findings_failed !== 1 ? 's' : ''} failed · attack-path weighted
                        </div>
                      </div>
                      <ScoreBadge pct={Math.round(assessScore)} />
                    </div>
                  </div>
                )}
              </div>

              <div className="prod-tiles">
                <StatTile label="Total Users"      value={summary.total_users}       sub={`${summary.enabled_users} enabled`} />
                <StatTile label="MFA Enabled"      value={summary.mfa_enabled_count} sub={summary.no_mfa_count > 0 ? `of ${(summary.mfa_enabled_count ?? 0) + summary.no_mfa_count} licensed · ${summary.no_mfa_count} without MFA` : `All ${summary.mfa_enabled_count ?? 0} licensed users`} color={summary.no_mfa_count > 0 ? '#ff2d55' : '#30d158'} tileColor={summary.no_mfa_count > 0 ? 'red' : 'green'} />
                <StatTile label="Global Admins"    value={summary.global_admin_users ?? summary.admin_users} color={summary.global_admin_users > 2 ? '#e6b800' : undefined} tileColor={summary.global_admin_users > 2 ? 'amber' : undefined} />
                <StatTile label="Guest Users"      value={summary.guest_users} />
                <StatTile label="Inactive Users"   value={summary.inactive_users}    color={summary.inactive_users > 0 ? '#e6b800' : undefined} tileColor={summary.inactive_users > 0 ? 'amber' : undefined} />
                <StatTile label="Risky Users"      value={summary.risky_users_count} color={summary.risky_users_count > 0 ? '#ff2d55' : '#30d158'} tileColor={summary.risky_users_count > 0 ? 'red' : 'green'} />
              </div>

              {/* CA policy status strip */}
              {caSummary.available && (
                <div className="resource-panel" style={{ marginTop: 12 }}>
                  <div className="resource-panel-header" style={{ marginBottom: 12 }}>
                    <div className="resource-panel-title">Conditional Access</div>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{caSummary.total} polic{caSummary.total === 1 ? 'y' : 'ies'}{caSummary.total > 0 ? ` · ${caSummary.enabled ?? 0} enforced${caSummary.report_only ? `, ${caSummary.report_only} report only` : ''}` : ''}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
                    {[
                      { key: 'mfa_all',          label: 'MFA for all users',   ok: caSummary.has_mfa_policy },
                      { key: 'admin_mfa',        label: 'Admin MFA policy',    ok: caSummary.has_admin_mfa_policy },
                      { key: 'legacy_auth',      label: 'Legacy auth blocked', ok: caSummary.has_block_legacy_auth },
                      { key: 'compliant_device', label: 'Device compliance',   ok: caSummary.has_compliant_device },
                    ].map(({ key, label, ok }) => {
                      // Enforced, report-only (set up but not switched on) or missing; older results have only ok
                      const st = caSummary.checks?.[key] || (ok ? 'enforced' : 'missing');
                      const [cls, word] = st === 'enforced' ? ['tag-green', 'Enforced'] : st === 'report_only' ? ['tag-amber', 'Report only'] : ['tag-red', 'Missing'];
                      return (
                        <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 10px', background: 'var(--surface)', borderRadius: 6 }}>
                          <span style={{ fontSize: 11, color: 'var(--text)' }}>{label}</span>
                          <span className={`tag ${cls}`}>{word}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="resource-panel">
                <div className="resource-panel-header">
                  <div>
                    <div className="resource-panel-title">Storage</div>
                    <div className="resource-panel-sub">Across mailboxes, OneDrive and SharePoint</div>
                  </div>
                </div>
                <div className="prod-storage-grid">
                  <div className="prod-storage-item"><div className="prod-storage-label">Mailboxes</div><div className="prod-storage-value">{summary.mailbox_total_fmt || '—'}</div></div>
                  <div className="prod-storage-item"><div className="prod-storage-label">OneDrive</div><div className="prod-storage-value">{summary.onedrive_total_fmt || '—'}</div></div>
                  <div className="prod-storage-item"><div className="prod-storage-label">SharePoint</div><div className="prod-storage-value">{summary.sp_total_fmt || '—'}</div></div>
                  {storage.total && (
                    <div className="prod-storage-item">
                      <div className="prod-storage-label">Tenant Quota</div>
                      <div className="prod-storage-value">{storage.used} / {storage.total}</div>
                      <div className="prod-storage-bar">
                        <div className="prod-storage-fill" style={{ width: `${Math.min(storage.pct, 100)}%`, background: storage.pct > 80 ? '#ff2d55' : storage.pct > 60 ? '#e6b800' : 'var(--accent)' }} />
                      </div>
                      <div className="prod-storage-pct">{storage.pct}% used</div>
                    </div>
                  )}
                </div>
                {summary.usage_reports?.names_concealed && (
                  <div className="resource-panel-sub" style={{ marginTop: 10 }}>
                    Totals only: Microsoft 365 hides user names in its usage reports, so each person's mailbox and OneDrive size isn't shown. Turn this off in the Microsoft 365 admin centre under Settings, Org settings, Reports.
                  </div>
                )}
              </div>

              {(data.no_mfa_users || []).length > 0 && (
                <div className="prod-alert-box">
                  <div className="prod-alert-title">Users without MFA</div>
                  <div className="prod-alert-list">
                    {(data.no_mfa_users || []).map((u, i) => (
                      <div key={i} className="prod-alert-user">
                        <span className="prod-alert-name">{u.name || u.upn}</span>
                        <span className="prod-alert-upn">{u.upn}</span>
                        {u.is_global_admin && <span className="prod-tag-admin">Global Admin</span>}
                        {!u.is_global_admin && u.is_admin && <span className="prod-tag-admin">Admin</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Security ── */}
          {activeTab === 'security' && (
            <div>
              {/* Summary row */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
                {[
                  { label: 'Assessment Score', value: assessScore != null ? `${assessScore}/100` : '—', color: assessColor },
                  { label: 'Failed Findings',  value: summary.findings_failed ?? failedFindings.length, color: failedFindings.length > 0 ? '#ff2d55' : '#30d158' },
                  { label: 'Critical',         value: summary.findings_critical ?? criticalCount, color: criticalCount > 0 ? '#ff2d55' : 'var(--text-muted)' },
                  { label: 'High',             value: summary.findings_high ?? failedFindings.filter(f => f.severity === 'high').length, color: '#ff6b35' },
                  { label: 'Passed',           value: summary.findings_passed ?? findings.filter(f => f.passed).length, color: '#30d158' },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', minWidth: 100 }}>
                    <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-mono)', color }}>{value}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{label}</div>
                  </div>
                ))}
              </div>

              {/* Failed findings first */}
              {failedFindings.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 10 }}>
                    Findings requiring action
                  </div>
                  {['critical','high','medium','low'].map(sev =>
                    failedFindings.filter(f => f.severity === sev).map(f => (
                      <FindingCard key={f.id} finding={f} />
                    ))
                  )}
                </div>
              )}

              {/* Passed findings — collapsed by default */}
              {findings.filter(f => f.passed).length > 0 && (
                <PassedFindingsSection findings={findings.filter(f => f.passed)} />
              )}

              {findings.length === 0 && (
                <div className="prod-empty">
                  <div className="prod-empty-title">No findings data</div>
                  <div className="prod-empty-sub">Run a refresh to generate the security assessment.</div>
                </div>
              )}
            </div>
          )}

          {/* ── Identity ── */}
          {activeTab === 'identity' && (
            <div>
              {/* Security Defaults */}
              <div className="resource-panel" style={{ marginBottom: 12 }}>
                <div className="resource-panel-header">
                  <div>
                    <div className="resource-panel-title">Security Defaults</div>
                    <div className="resource-panel-sub">Microsoft's baseline protection settings</div>
                  </div>
                  <StatusPill ok={sdEnabled} trueLabel="Enabled" falseLabel="Disabled" nullLabel="Unknown" />
                </div>
                {sdEnabled === false && caSummary.total > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
                    Security Defaults are disabled — Conditional Access policies are in use instead.
                  </div>
                )}
                {sdEnabled === false && caSummary.total === 0 && (
                  <div style={{ fontSize: 11, color: '#ff2d55', marginTop: 8, fontWeight: 600 }}>
                    Security Defaults are disabled and no Conditional Access policies exist.
                  </div>
                )}
              </div>

              {/* Global Admins */}
              {globalAdmins.length > 0 && (
                <div className="resource-panel" style={{ marginBottom: 12 }}>
                  <div className="resource-panel-header" style={{ marginBottom: 10 }}>
                    <div>
                      <div className="resource-panel-title">Global Administrators</div>
                      <div className="resource-panel-sub">{globalAdmins.length} account{globalAdmins.length !== 1 ? 's' : ''} with full tenant control</div>
                    </div>
                    {adminsNoMfa.filter(u => u.is_global_admin).length > 0 && (
                      <span style={{ fontSize: 11, color: '#ff2d55', fontWeight: 600 }}>
                        {adminsNoMfa.filter(u => u.is_global_admin).length} without MFA
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {globalAdmins.map((u, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', background: 'var(--surface)', borderRadius: 6 }}>
                        <span style={{ flex: 1, fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>{u.upn}</span>
                        <StatusPill ok={u.mfa_enabled} trueLabel="MFA" falseLabel="No MFA" />
                        {u.last_signin && <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Last seen {fmtDateShort(u.last_signin)}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* CA Policies */}
              <div className="resource-panel" style={{ marginBottom: 12 }}>
                <div className="resource-panel-header" style={{ marginBottom: caPolicies.length > 0 ? 12 : 0 }}>
                  <div>
                    <div className="resource-panel-title">Conditional Access Policies</div>
                    <div className="resource-panel-sub">
                      {caSummary.total ?? 0} total · {caSummary.enabled ?? 0} enabled · {caSummary.report_only ?? 0} report-only
                    </div>
                  </div>
                  {!caSummary.available && (
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Requires Policy.Read.All permission</span>
                  )}
                </div>
                {caPolicies.length > 0 && (
                  <div className="prod-table-wrap">
                    <table className="prod-table">
                      <thead>
                        <tr>
                          <th>Policy Name</th>
                          <th>State</th>
                          <th>MFA</th>
                          <th>Blocks Legacy</th>
                          <th>Device Compliance</th>
                          <th>Scope</th>
                        </tr>
                      </thead>
                      <tbody>
                        {caPolicies.map((p, i) => {
                          const stateColor = p.state === 'enabled' ? '#30d158' : p.state === 'enabledForReportingButNotEnforced' ? '#e6b800' : '#86868b';
                          const stateLabel = p.state === 'enabled' ? 'Enabled' : p.state === 'enabledForReportingButNotEnforced' ? 'Report-only' : 'Disabled';
                          return (
                            <tr key={i}>
                              <td style={{ fontWeight: 500 }}>{p.name}</td>
                              <td><span style={{ fontSize: 10, fontWeight: 700, color: stateColor }}>{stateLabel}</span></td>
                              <td>{p.requires_mfa ? <span className="prod-tag-ok">Yes</span> : '—'}</td>
                              <td>{p.blocks_legacy_auth ? <span className="prod-tag-ok">Yes</span> : '—'}</td>
                              <td>{p.requires_compliant_device ? <span className="prod-tag-ok">Yes</span> : '—'}</td>
                              <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                {p.targets_all_users ? 'All users' : p.targets_admin_roles ? 'Admin roles' : 'Custom'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {caSummary.available && caPolicies.length === 0 && (
                  <div style={{ fontSize: 12, color: '#ff2d55', fontWeight: 600, padding: '8px 0' }}>
                    No Conditional Access policies found.
                  </div>
                )}
              </div>

              {/* Legacy auth */}
              {(legacyUsers.length > 0 || summary.legacy_auth_detected != null) && (
                <div className="resource-panel" style={{ marginBottom: 12 }}>
                  <div className="resource-panel-header" style={{ marginBottom: legacyUsers.length > 0 ? 12 : 0 }}>
                    <div>
                      <div className="resource-panel-title">Legacy Authentication</div>
                      <div className="resource-panel-sub">Sign-ins using non-modern auth protocols (last 30 days)</div>
                    </div>
                    <StatusPill ok={legacyUsers.length === 0} trueLabel="None detected" falseLabel={`${legacyUsers.length} user${legacyUsers.length !== 1 ? 's' : ''}`} />
                  </div>
                  {legacyUsers.length > 0 && (
                    <div className="prod-table-wrap">
                      <table className="prod-table">
                        <thead><tr><th>User</th><th>Client App</th><th>Application</th><th>Last Seen</th></tr></thead>
                        <tbody>
                          {legacyUsers.map((u, i) => (
                            <tr key={i}>
                              <td className="prod-mono">{u.upn}</td>
                              <td style={{ fontSize: 11 }}>{u.client_app || '—'}</td>
                              <td style={{ fontSize: 11 }}>{u.app || '—'}</td>
                              <td className="prod-mono">{fmtDateShort(u.last_seen)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ── Monitored Accounts ── always visible when accounts configured ── */}
              {watchedAccounts.length > 0 && (
                <div className="resource-panel" style={{ marginBottom: 12 }}>
                  <div className="resource-panel-header" style={{ marginBottom: 12 }}>
                    <div>
                      <div className="resource-panel-title">Monitored Accounts</div>
                      <div className="resource-panel-sub">
                        {watchedAccounts.length} account{watchedAccounts.length !== 1 ? 's' : ''} under watch
                        {' · '}alerts via
                        {alertCfg?.email_alerts !== false ? ' Email' : ''}
                        {alertCfg?.email_alerts !== false && (alertCfg?.teams_alerts !== false && alertCfg?.teams_webhook_url) ? ' and' : ''}
                        {alertCfg?.teams_alerts !== false && alertCfg?.teams_webhook_url ? ' Teams' : ''}
                        {alertCfg?.email_alerts === false && !(alertCfg?.teams_alerts !== false && alertCfg?.teams_webhook_url) ? ' — no channels active' : ''}
                      </div>
                    </div>
                    <span style={{ fontSize: 11, color: watchedSignins.length > 0 ? 'var(--text-muted)' : '#30d158' }}>
                      {watchedSignins.length > 0
                        ? `${watchedSignins.length} event${watchedSignins.length !== 1 ? 's' : ''} recorded`
                        : 'No sign-ins detected'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {watchedAccounts.map((acct, i) => {
                      const recentEvents = watchedSignins.filter(e => e.upn === acct.upn).slice(0, 1);
                      const lastSeen     = recentEvents[0]?.ts;
                      const lastAlerted  = watchedSignins.filter(e => e.upn === acct.upn && e.alerted).slice(0, 1)[0]?.ts;
                      const activeFlags  = [
                        acct.alert_any_login       && 'Any sign-in',
                        acct.alert_failed          && 'Failed attempts',
                        acct.alert_out_of_hours    && `Out of hours (${acct.business_hours_start}:00–${acct.business_hours_end}:00 UTC)`,
                        acct.alert_foreign_country && `Foreign country (${(acct.allowed_countries || []).join(', ')})`,
                        acct.alert_new_ip          && 'New IP',
                      ].filter(Boolean);
                      return (
                        <div key={i} style={{
                          background: 'var(--surface)', border: '1px solid var(--border)',
                          borderRadius: 7, padding: '10px 14px',
                          display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap',
                        }}>
                          {/* Identity */}
                          <div style={{ minWidth: 180, flex: 1 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>
                              {acct.label || acct.upn}
                            </div>
                            {acct.label && acct.label !== acct.upn && (
                              <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', marginTop: 2 }}>{acct.upn}</div>
                            )}
                          </div>
                          {/* Alert conditions */}
                          <div style={{ flex: 2, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                            {activeFlags.length > 0
                              ? activeFlags.map((f, fi) => (
                                  <span key={fi} style={{
                                    fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
                                    background: 'var(--accent)15', border: '1px solid var(--accent)30',
                                    color: 'var(--accent)',
                                  }}>{f}</span>
                                ))
                              : <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>No conditions configured</span>}
                          </div>
                          {/* Status */}
                          <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 120 }}>
                            {lastSeen ? (
                              <>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Last seen {fmtDateShort(lastSeen)}</div>
                                {lastAlerted && (
                                  <div style={{ fontSize: 10, color: '#e6b800', marginTop: 2 }}>Alert fired {fmtDateShort(lastAlerted)}</div>
                                )}
                              </>
                            ) : (
                              <div style={{ fontSize: 11, color: '#30d158', fontWeight: 600 }}>No sign-ins recorded</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Alert channel status */}
                  <div style={{
                    marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border)',
                    display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
                  }}>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Alert channels:</span>
                    {[
                      { label: 'Email',  active: alertCfg?.email_alerts !== false },
                      { label: 'Teams',  active: alertCfg?.teams_alerts !== false && !!alertCfg?.teams_webhook_url,
                                         warn: alertCfg?.teams_alerts !== false && !alertCfg?.teams_webhook_url },
                    ].map(({ label, active, warn }) => {
                      const color = active ? '#30d158' : warn ? '#e6b800' : '#86868b';
                      const icon  = active ? '●' : warn ? '◐' : '○';
                      return (
                        <span key={label} style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                          background: `${color}18`, border: `1px solid ${color}40`, color,
                        }}>
                          {icon} {label}{warn ? ' — no webhook' : !active ? ' off' : ''}
                        </span>
                      );
                    })}
                    {isAdmin && (
                      <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 'auto' }}>
                        Configure in Client Settings
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* ── Watched Account Activity ── */}
              {watchedSignins.length > 0 && (
                <div className="resource-panel" style={{ marginBottom: 12 }}>
                  <div className="resource-panel-header" style={{ marginBottom: 12 }}>
                    <div>
                      <div className="resource-panel-title">Watched Account Activity</div>
                      <div className="resource-panel-sub">Sign-ins for monitored accounts · live · refreshes every 2 minutes</div>
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {watchedSignins.filter(e => e.alerted).length} alert{watchedSignins.filter(e => e.alerted).length !== 1 ? 's' : ''} fired
                    </span>
                  </div>
                  <div className="prod-table-wrap">
                    <table className="prod-table">
                      <thead>
                        <tr>
                          <th>Account</th>
                          <th>Time</th>
                          <th>Status</th>
                          <th>IP</th>
                          <th>Location</th>
                          <th>App</th>
                          <th>Triggers</th>
                        </tr>
                      </thead>
                      <tbody>
                        {watchedSignins.slice(0, 100).map((e, i) => {
                          const alertColor = !e.success ? '#ff2d55' : e.triggers?.length > 0 ? '#e6b800' : 'var(--text-muted)';
                          return (
                            <tr key={i}>
                              <td>
                                <div style={{ fontSize: 12, fontWeight: 600 }}>{e.label || e.upn}</div>
                                {e.label && e.label !== e.upn && (
                                  <div className="prod-mono" style={{ fontSize: 10, color: 'var(--text-muted)' }}>{e.upn}</div>
                                )}
                              </td>
                              <td className="prod-mono">{fmtDateShort(e.ts)}</td>
                              <td>
                                <span style={{ fontSize: 10, fontWeight: 700, color: e.success ? '#30d158' : '#ff2d55' }}>
                                  {e.success ? 'Success' : `Failed (${e.error_code})`}
                                </span>
                              </td>
                              <td className="prod-mono" style={{ fontSize: 11 }}>{e.ip || '—'}</td>
                              <td style={{ fontSize: 11 }}>{[e.city, e.country].filter(Boolean).join(', ') || '—'}</td>
                              <td style={{ fontSize: 11 }}>{e.app || '—'}</td>
                              <td>
                                {e.triggers?.length > 0 ? (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                    {e.triggers.map((t, ti) => (
                                      <span key={ti} style={{
                                        fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                                        background: `${alertColor}18`, border: `1px solid ${alertColor}40`,
                                        color: alertColor, whiteSpace: 'nowrap',
                                      }}>{t}</span>
                                    ))}
                                  </div>
                                ) : (
                                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>No triggers</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {watchedSignins.length > 100 && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, textAlign: 'center' }}>
                      Showing 100 of {watchedSignins.length} events
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Users ── */}
          {activeTab === 'users' && (
            <div className="resource-panel">
              <div className="resource-panel-header">
                <div><div className="resource-panel-title">Users</div><div className="resource-panel-sub">{users.length} total · {summary.enabled_users} enabled</div></div>
                <input className="prod-search" placeholder="Search name, email, department…" value={userFilter} onChange={e => setUserFilter(e.target.value)} />
              </div>
              <div className="prod-table-wrap">
                <table className="prod-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>UPN</th>
                      <th>MFA</th>
                      <th>Role</th>
                      <th>Last Sign-in</th>
                      <th>Mailbox</th>
                      <th>OneDrive</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u, i) => (
                      <tr key={i} className={!u.enabled ? 'prod-row-disabled' : ''}>
                        <td>
                          <div className="prod-user-name">{u.name}</div>
                          {u.department && <div className="prod-user-dept">{u.department}</div>}
                        </td>
                        <td className="prod-mono">{u.upn}</td>
                        <td>{u.mfa_enabled ? <span className="prod-tag-ok">✓ {(u.mfa_methods || []).join(', ') || 'Enabled'}</span> : <span className="prod-tag-warn">✗ None</span>}</td>
                        <td>
                          {u.is_global_admin
                            ? <span className="prod-tag-admin">Global Admin</span>
                            : u.is_admin
                              ? <span className="prod-tag-admin" style={{ opacity: 0.7 }}>Admin</span>
                              : '—'}
                        </td>
                        <td className="prod-mono">{fmtDateShort(u.last_signin) || '—'}</td>
                        <td className="prod-mono">{u.mailbox_storage || '—'}</td>
                        <td className="prod-mono">{u.onedrive_storage || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Licences ── */}
          {activeTab === 'licences' && (
            <div className="resource-panel">
              <div className="resource-panel-header"><div className="resource-panel-title">Licences</div></div>
              <div className="prod-table-wrap">
                <table className="prod-table">
                  <thead><tr><th>Product</th><th>SKU</th><th>Assigned</th><th>Total</th><th>Available</th><th>Status</th></tr></thead>
                  <tbody>
                    {licences.map((l, i) => (
                      <tr key={i}>
                        <td>{l.name || l.sku}</td>
                        <td className="prod-mono" style={{ fontSize: 11 }}>{l.sku}</td>
                        <td className="prod-mono">{l.assigned ?? '—'}</td>
                        <td className="prod-mono">{l.total >= 1000000 ? '∞' : l.total}</td>
                        <td className="prod-mono" style={{ color: l.available <= 0 && l.total > 0 && l.total < 1000000 ? '#ff2d55' : l.available <= 2 && l.total > 0 && l.total < 1000000 ? '#e6b800' : undefined }}>
                          {l.total >= 1000000 ? '∞' : l.available}
                        </td>
                        <td>{l.assigned > 0 ? <span className="prod-tag-ok">Active</span> : <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>Unassigned</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── SharePoint ── */}
          {activeTab === 'sharepoint' && (
            <div className="resource-panel">
              <div className="resource-panel-header"><div><div className="resource-panel-title">SharePoint Sites</div><div className="resource-panel-sub">{spSites.length} sites</div></div></div>
              <div className="prod-table-wrap">
                <table className="prod-table">
                  <thead><tr><th>Site</th><th>URL</th><th>Storage Used</th><th>Last Modified</th></tr></thead>
                  <tbody>
                    {spSites.map((s, i) => (
                      <tr key={i}>
                        <td>{s.name}</td>
                        <td className="prod-mono" style={{ fontSize: 11 }}>{s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>{s.url}</a> : '—'}</td>
                        <td className="prod-mono">{s.storage_fmt || '—'}</td>
                        <td className="prod-mono">{fmtDateShort(s.last_modified) || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Devices (Intune) ── */}
          {activeTab === 'devices' && intuneSummary.available && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10, marginBottom: 16 }}>
                {[
                  { label: 'Total Devices',  value: intuneSummary.total,         color: undefined },
                  { label: 'Compliant',      value: intuneSummary.compliant,     color: '#30d158' },
                  { label: 'Non-compliant',  value: intuneSummary.noncompliant,  color: intuneSummary.noncompliant > 0 ? '#ff2d55' : '#30d158' },
                  { label: 'BitLocker On',   value: intuneSummary.bitlocker_enabled, color: undefined },
                  { label: 'Defender On',    value: intuneSummary.defender_enabled,  color: undefined },
                  { label: 'Policies',       value: intuneSummary.compliance_policies, color: intuneSummary.compliance_policies === 0 ? '#ff2d55' : undefined },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
                    <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'var(--font-mono)', color: color || 'var(--text)' }}>{value ?? '—'}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{label}</div>
                  </div>
                ))}
              </div>
              <div className="resource-panel">
                <div className="resource-panel-header" style={{ marginBottom: 12 }}>
                  <div className="resource-panel-title">Managed Devices</div>
                  {noncompliantDev.length > 0 && (
                    <span style={{ fontSize: 11, color: '#ff2d55', fontWeight: 600 }}>{noncompliantDev.length} non-compliant</span>
                  )}
                </div>
                <div className="prod-table-wrap">
                  <table className="prod-table">
                    <thead>
                      <tr><th>Device</th><th>User</th><th>OS</th><th>Compliance</th><th>Encrypted</th><th>Defender</th><th>Last Sync</th></tr>
                    </thead>
                    <tbody>
                      {intuneDevices.map((d, i) => {
                        const compColor = d.compliance === 'compliant' ? '#30d158' : d.compliance === 'noncompliant' ? '#ff2d55' : '#86868b';
                        return (
                          <tr key={i}>
                            <td style={{ fontWeight: 500 }}>{d.device_name || '—'}</td>
                            <td className="prod-mono" style={{ fontSize: 11 }}>{d.upn || '—'}</td>
                            <td style={{ fontSize: 11 }}>{d.os}{d.os_version ? ` ${d.os_version}` : ''}</td>
                            <td><span style={{ fontSize: 10, fontWeight: 700, color: compColor, textTransform: 'capitalize' }}>{d.compliance}</span></td>
                            <td>{d.encrypted ? <span className="prod-tag-ok">Yes</span> : <span className="prod-tag-warn">No</span>}</td>
                            <td>{d.defender_on ? <span className="prod-tag-ok">On</span> : <span className="prod-tag-warn">Off</span>}</td>
                            <td className="prod-mono">{fmtDateShort(d.last_sync) || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── Apps (OAuth grants) ── */}
          {activeTab === 'apps' && (
            <div className="resource-panel">
              <div className="resource-panel-header" style={{ marginBottom: 12 }}>
                <div>
                  <div className="resource-panel-title">App Consent Grants</div>
                  <div className="resource-panel-sub">{oauthGrants.length} tenant-wide consent{oauthGrants.length !== 1 ? 's' : ''} · {highRiskGrants.length} high-risk</div>
                </div>
              </div>
              <div className="prod-table-wrap">
                <table className="prod-table">
                  <thead><tr><th>Application</th><th>Risk</th><th>Permissions</th><th>Sensitive Scopes</th></tr></thead>
                  <tbody>
                    {oauthGrants.map((g, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 500 }}>{g.app_name || '—'}</td>
                        <td>{g.is_high_risk ? <span className="prod-tag-admin">High risk</span> : <span className="prod-tag-ok">Low risk</span>}</td>
                        <td className="prod-mono" style={{ fontSize: 10 }}>{g.scope_count} scope{g.scope_count !== 1 ? 's' : ''}</td>
                        <td style={{ fontSize: 11 }}>
                          {g.risky_scopes?.length > 0
                            ? <span style={{ color: '#e6b800' }}>{g.risky_scopes.join(', ')}</span>
                            : <span style={{ color: 'var(--text-muted)' }}>None</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Risky Users ── */}
          {activeTab === 'risky' && (
            <div className="resource-panel">
              <div className="resource-panel-header"><div className="resource-panel-title">Risky Users</div></div>
              <div className="prod-table-wrap">
                <table className="prod-table">
                  <thead><tr><th>UPN</th><th>Risk Level</th><th>Risk State</th></tr></thead>
                  <tbody>
                    {riskyUsers.map((u, i) => (
                      <tr key={i}>
                        <td className="prod-mono">{u.upn}</td>
                        <td><span className={`prod-tag-${u.risk_level === 'high' ? 'admin' : 'warn'}`}>{u.risk_level}</span></td>
                        <td>{u.risk_state}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Passed findings collapsible section ───────────────────────────────────────

function PassedFindingsSection({ findings }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button className="btn btn-quiet btn-sm"
        onClick={() => setOpen(o => !o)}
       
      >
        <span>{open ? '▲' : '▼'}</span>
        <span>{findings.length} passed check{findings.length !== 1 ? 's' : ''}</span>
      </button>
      {open && findings.map(f => <FindingCard key={f.id} finding={f} />)}
    </div>
  );
}

// ── Google Workspace Tenant View ───────────────────────────────────────────────

function GoogleTenantView({ isAdmin }) {
  return (
    <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
      Google Workspace integration coming soon.
    </div>
  );
}

// ── SaaS Discovery View ────────────────────────────────────────────────────────

const CATEGORY_ORDER = [
  'Productivity', 'Communication', 'Security', 'IT Management',
  'Finance', 'HR', 'Marketing', 'CRM', 'Development', 'Storage', 'Other',
];

function SaasDiscoveryView({ slug, isAdmin }) {
  const [services, setServices]   = useState([]);
  const [lastRun, setLastRun]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    authFetch(`/api/clients/${slug}/saas`)
      .then(r => r.json())
      .then(d => {
        if (d.error) { setError(d.error); return; }
        const payload = d.data || {};
        setServices(payload.services || []);
        setLastRun(payload.scanned_at || null);
      })
      .catch(() => setError('Failed to load SaaS data'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="resource-loading">Loading SaaS discovery data…</div>;
  if (error)   return <div className="resource-error" style={{ marginTop: 16 }}>{error}</div>;

  if (services.length === 0) return (
    <div className="prod-empty">
      <div className="prod-empty-title">No SaaS services discovered yet</div>
      <div className="prod-empty-sub">SaaS discovery runs automatically and will appear here once data is available.</div>
    </div>
  );

  const categoriesInData = [...new Set(services.map(s => s.category || 'Other'))];
  const orderedCategories = [
    ...CATEGORY_ORDER.filter(c => categoriesInData.includes(c)),
    ...categoriesInData.filter(c => !CATEGORY_ORDER.includes(c)).sort(),
  ];
  const categories = ['All', ...orderedCategories];

  const filtered = services.filter(s => {
    const matchCat = activeCategory === 'All' || (s.category || 'Other') === activeCategory;
    const q = search.toLowerCase();
    const matchSearch = !q || s.name?.toLowerCase().includes(q) || s.category?.toLowerCase().includes(q);
    return matchCat && matchSearch;
  });

  const grouped = filtered.reduce((acc, s) => {
    const cat = s.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});

  const displayGroups = activeCategory === 'All'
    ? [...CATEGORY_ORDER.filter(c => grouped[c]), ...Object.keys(grouped).filter(c => !CATEGORY_ORDER.includes(c)).sort()]
    : [activeCategory];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {lastRun ? `Updated ${fmtDate(lastRun)}` : `${services.length} services discovered`}
        </div>
        <input className="prod-search" placeholder="Search services…" value={search} onChange={e => setSearch(e.target.value)} style={{ width: 200 }} />
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 24 }}>
        {categories.map(cat => (
          <button className="btn btn-secondary btn-sm" aria-pressed={activeCategory === cat} key={cat} onClick={() => setActiveCategory(cat)}>
            {cat}
            {cat !== 'All' && (
              <span style={{ marginLeft: 5, fontSize: 10, opacity: 0.7 }}>
                {services.filter(s => (s.category || 'Other') === cat).length}
              </span>
            )}
          </button>
        ))}
      </div>
      {filtered.length === 0 && (
        <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No services match your search.</div>
      )}
      {displayGroups.map(cat => grouped[cat] && (
        <div key={cat} style={{ marginBottom: 28 }}>
          {activeCategory === 'All' && (
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 10 }}>{cat}</div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
            {grouped[cat].map((s, i) => <SaasCard key={i} service={s} isAdmin={isAdmin} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

function SaasCard({ service, isAdmin }) {
  const [expanded, setExpanded] = useState(false);
  const hasAdminDetail = isAdmin && (service.matched_signals?.length > 0 || service.confidence);
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
      padding: '14px 16px', cursor: hasAdminDetail ? 'pointer' : 'default', transition: 'border-color 0.15s',
    }} onClick={() => hasAdminDetail && setExpanded(e => !e)}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 2 }}>{service.name}</div>
          {service.description && <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>{service.description}</div>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
          {service.category && (
            <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: 'var(--accent)15', color: 'var(--accent)', border: '1px solid var(--accent)30', whiteSpace: 'nowrap' }}>{service.category}</span>
          )}
          {hasAdminDetail && <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{expanded ? '▲' : '▼'}</span>}
        </div>
      </div>
      {isAdmin && expanded && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: service.matched_signals?.length > 0 ? 10 : 0 }}>
            {service.confidence && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Confidence: <span style={{ color: 'var(--text)', fontWeight: 600, textTransform: 'capitalize' }}>{String(service.confidence)}</span></div>}
            {service.matched_signals?.length > 0 && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Signals: <span style={{ color: 'var(--text)', fontWeight: 600 }}>{service.matched_signals.length}</span></div>}
          </div>
          {service.matched_signals?.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {service.matched_signals.map((sig, i) => {
                const type = typeof sig === 'object' ? String(sig.type || '') : '';
                const matched = typeof sig === 'object' ? String(sig.matched || '') : String(sig);
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 11 }}>
                    {type && <span style={{ color: 'var(--accent)', fontFamily: 'var(--font-mono)', fontSize: 10, flexShrink: 0 }}>{type}</span>}
                    <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>{matched}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main ProductivityHub ───────────────────────────────────────────────────────

export default function ProductivityHub({ slug }) {
  const { user } = useAuth();
  const isAdmin  = user?.role === 'admin';

  const [tenants, setTenants]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState(null);

  useEffect(() => {
    authFetch(`/api/clients/${slug}/productivity/tenants`)
      .then(r => r.json())
      .then(d => {
        const visible = (d.tenants || []).filter(t => isAdmin || t.provider === 'm365');
        setTenants(visible);
        setActiveTab(visible.length > 0 ? visible[0].id : 'saas');
        setLoading(false);
      })
      .catch(() => { setLoading(false); setActiveTab('saas'); });
  }, [slug, isAdmin]);

  if (loading) return <div style={{ padding: 32, textAlign: 'center' }}><span className="spinner" /></div>;

  return (
    <div>
      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .prod-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none; }
        .prod-table-wrap::-webkit-scrollbar { display: none; }
        .prod-table { min-width: 480px; }
        @media (max-width: 600px) {
          .prod-tiles { grid-template-columns: repeat(2, 1fr) !important; }
          .prod-storage-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>

      <div style={{ display: 'flex', gap: 2, marginBottom: 24, borderBottom: '1px solid var(--border)', overflowX: 'auto', scrollbarWidth: 'none' }} className="hide-scrollbar">
        {tenants.map(t => {
          const isActive = activeTab === t.id;
          const displayName = t.tenant_name && t.tenant_name.trim() ? t.tenant_name : (t.tenant_id ? t.tenant_id.split('.')[0] : 'Unnamed');
          return (
            <button key={t.id} className="tab-btn" aria-selected={isActive} onClick={() => setActiveTab(t.id)}>
              {displayName}
              <ProviderPill provider={t.provider} />
            </button>
          );
        })}
        <button className="tab-btn" aria-selected={activeTab === 'saas'} onClick={() => setActiveTab('saas')}>
          SaaS Discovery
        </button>
      </div>

      {tenants.map(t => activeTab === t.id && (
        <div key={t.id}>
          {t.provider === 'm365'   && <M365TenantView slug={slug} tenantId={t.id} isAdmin={isAdmin} />}
          {t.provider === 'google' && <GoogleTenantView isAdmin={isAdmin} />}
        </div>
      ))}

      {activeTab === 'saas' && <SaasDiscoveryView slug={slug} isAdmin={isAdmin} />}
    </div>
  );
}
